// @flow
import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { RouteNames } from 'router';
import { HomeActions } from 'modules/home/actions';
import { HomeSelector } from 'modules/home/selector';
import { PageLoader } from 'components/molecules/pageLoader';
import FooterSection from 'components/templates/footer';
import PageTemplate from 'components/templates/pageTemplate';
import { StaticContentSection } from 'components/templates/staticContentSection';
import { useViewport } from 'components/templates/viewportProvider';
import type { AppState } from 'modules/rootReducer';
import type { SiteSettings, Vertical } from 'types/common';
import RenderSEO from 'components/atoms/renderSEO';
import { CmsPagesActions } from 'modules/cmsPages/actions';
import { CmsPagesSelector } from 'modules/cmsPages/selector';
import { FooterActions } from 'modules/footerLink/actions';
import { FooterSelector } from 'modules/footerLink/selector';

type Props = {
  isLoading: boolean,
  verticals: Array<Vertical>,
  getVerticals: () => void,
  siteSettings: SiteSettings,
  getSiteSettings: () => void,
};

const PrivacyPage = (props: Props) => {
  const {
    isLoading,
    getVerticals,
    verticals,
    getSiteSettings,
    siteSettings,
    getBannerData,
    getPageData,
    footerLink,
    getFooterLinkData,
    bannerData,
    cmsPageData,
  } = props;

  const metaTagSEO = {
    metaTitle: 'Privacy Policy - Total Environment',
    metaDescription:
      'With an innate drive to create enhanced living experience for people, Total Environment designs apartments, penthouses and villas that people actually want to live in. From live music to handcrafted furniture to fine craft beer, we leave our mark of excellence everywhere. To know more about our homes and handcrafted experiences, call + 91 99000 78000.',
    metaKeywords: '',
  };

  const { isMobile } = useViewport();
  let [theme, setTheme] = useState('light-theme');

  useEffect(() => {
    getVerticals();
    getSiteSettings();
    getBannerData();
    getPageData();
    getFooterLinkData();
  }, []);

  if (isLoading) {
    return <PageLoader isMobile={isMobile} />;
  }

  if (
    !siteSettings ||
    !footerLink ||
    !verticals ||
    !bannerData ||
    !cmsPageData
  ) {
    return null;
  }

  const componentList = [
    {
      component: StaticContentSection,
      data: { html: termsHTML, isScroll: true, toggleId: true },
      location: props,
      sectionId: 'privacy-policy',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Privacy Policy',
          breadcrumbLink: RouteNames.privacy,
        },
      ],
      theme: 'light-theme',
    },
    {
      component: FooterSection,
      theme: 'dark-theme',
      sectionId: 'te-links',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Privacy Policy',
          breadcrumbLink: RouteNames.privacy,
        },
        {
          breadcrumbName: 'Links',
          breadcrumbLink: '#',
        },
      ],
      data: { siteSettings, footerLink },
    },
  ];

  return (
    <>
      <RenderSEO metaTags={metaTagSEO} />
      <PageTemplate
        theme={theme}
        componentList={componentList}
        updateTheme={e => setTheme(e)}
        verticals={verticals}
        siteSettings={siteSettings}
        showGetInTouch={false}
        showShareButton={false}
      />
    </>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    isLoading:
      HomeSelector.getVerticalsLoading(state) ||
      FooterSelector.isLoading(state) ||
      HomeSelector.getSiteSettingsLoading(state),
    verticals: HomeSelector.getVerticals(state),
    siteSettings: HomeSelector.getSiteSettings(state),
    bannerData: HomeSelector.getBannerData(state),
    cmsPageData: CmsPagesSelector.getPageData(state),
    footerLink: FooterSelector.getFooterData(state),
  };
};

const mapDispatchToProps = dispatch => {
  const { getVerticals, getSiteSettings, getBannerData } = HomeActions;
  const { getPageData } = CmsPagesActions;
  const { getFooterLinkData } = FooterActions;
  return bindActionCreators(
    {
      getVerticals,
      getSiteSettings,
      getBannerData,
      getPageData,
      getFooterLinkData,
    },
    dispatch
  );
};
export default connect(mapStateToProps, mapDispatchToProps)(PrivacyPage);

const termsHTML = `
<div>
  <h1>Privacy Policy</h1>
  <div>
  <p>This Privacy Policy governs the manner in which Total Environment and its subsidiaries, partners, agents, and affiliates collect, use, maintain and disclose information collected from users of our website and microsite.</p>
  <h3>Personal identification information:</h3>
  <p>We may collect personal identification information from Users in a variety of ways, including, but not limited to, when Users visit our site, subscribe to the newsletter, fill out a form, and in connection with other activities, services, features or resources we make available on our Site. Users may be asked for, as appropriate, name, email address, mailing address, phone number. Users may, however, visit our Site anonymously. Users can always refuse to supply personal identification information, except that it may prevent them from engaging in certain Site related activities.</p>
 <h3>Non-personal identification information:</h3>
 <p>We may collect non-personal identification information about Users whenever they interact with our Site. Non-personal identification information may include the browser name, the type of computer and technical information about Users means the type of connection to our Sites, such as the operating system and the Internet service providers utilized and other similar information.</p>
  <h3>Web browser cookies:</h3>
  <p>Our Site may use "cookies" to enhance User experience. User's web browser places cookies on their hard drive for record-keeping purposes and sometimes to track information about them. Users may choose to set their web browser to refuse cookies or to alert the Users when cookies are being sent. If they do so, note that some parts of the Site may not function properly. This is a standard operating procedure that is used across the internet.</p>
  <h3>How we use collected information:</h3>
  <p>Total Environment may collect and use User&apos;s personal information for the following purposes:</p>
  <div>
    <ul>
        <li>To improve customer service:</li>
        <li>Information provided by Users helps us respond to customer service requests and support needs, more efficiently.</li>
        <li>To personalize User experience:</li>
        <li>We may use information in the aggregate to understand how our Users as a group use the services and resources provided on our Site.</li>
        <li>To improve our Site:</li>
        <li>We may use feedback provided by the User/s to improve our products and services.</li>
        <li>To run a promotion, contest, survey or other Site feature.</li>
        <li>To send the User/s information they agreed to receive about topics of interest to them.</li>
        <li>To send periodic emails.</li>
    </ul>
    </div>
   <p>We may use the email address to respond to the inquiries, questions, and/or other requests of Users. If Users decide to opt to be part of our mailing list, then the Users will receive emails about company news, updates, related product or service information, etc. If at any time the User's would like to unsubscribe from receiving future emails, they may do so by contacting us via our Site.</p>
  <h3>How we protect User&apos;s information:</h3>
  <p>We adopt appropriate data collection, storage, and processing practices and security measures to protect against unauthorized access, alteration, disclosure or destruction of User&apos;s personal information and data stored on our Site. As with data security, there are limits to its effectiveness and we indemnify ourselves in the event of an attack that is difficult to defend against. We also will do our best to retrieve any data that is lost as per available resources.</p>
 <h3>Sharing personal information of Users:</h3>
 <p>We do not sell, trade, or rent User&apos;s personal identification information to others. We may share generic aggregated demographic information not linked to any personal identification information regarding User&apos;s with our subsidiaries, our business partners, trusted affiliates and advertisers for the purposes outlined above.</p>
 <h3>Changes to this privacy policy:</h3>
 <p>Total Environment shall update this privacy policy at its sole discretion. Users are advised to check this page for any changes in the privacy policy and to stay informed about how the personal information of the Users is protected by us. The User hereby acknowledges and agree that it is their responsibility to review this privacy policy periodically and become aware of modifications.</p>
  <h3>Your acceptance of these terms:</h3>
  <p>By using this Site, the Users signify their acceptance of this policy as may be modified from time to time. Users are advised not to access this site if they do not agree to our privacy policy. The abovementioned privacy policy shall be applicable to the information and data collected by our call centers as well.</p>
 <h3>Contacting us:</h3>
 <p>If you have any questions about this Privacy Policy, the practices of this site, or your dealings with this site, please contact us at Total Environment, Bangalore (9900078000).</p>
  </div>
</div>
`;
